import type { QueryClient } from "@tanstack/react-query"

import { qk } from "@/shared/api/queryKeys"
import {
  getProfileMeta,
  moveProfileMeta,
  patchProfileMeta,
  removeProfileMeta,
  setProfileMeta,
} from "@/entities/profile"
import { remapPendingAudio } from "./remapPendingAudio"
import type { Profile } from "./types"

type ProfilesListResult = { profiles: Profile[] }

export type AdoptRenamedProfileArgs = {
  profileId: string
  newProfileName: string
  setSelectedProfileId: (updater: (prev: string) => string) => void
  /** Defaults to success. Use processing to keep tour hold UI. */
  completeStatus?: "success" | "processing"
  /**
   * When true, move the profile id to newProfileName but keep the existing
   * list title (tour hold: API key = FIO, header still shows timestamp).
   */
  preserveTitle?: boolean
}

/**
 * Apply a completed-task rename using a fresh profiles snapshot from the
 * Query cache (never a render-time closure). Moves meta, remaps pending
 * audio, updates the list + selection, and removesQueries on the old id
 * so in-flight setMeta(oldId) cannot leave a phantom.
 */
export function adoptRenamedProfile(
  qc: QueryClient,
  {
    profileId,
    newProfileName,
    setSelectedProfileId,
    completeStatus = "success",
    preserveTitle = false,
  }: AdoptRenamedProfileArgs,
): string {
  const profiles =
    qc.getQueryData<ProfilesListResult>(qk.profiles.list())?.profiles ?? []
  const currentProfile = profiles.find((p) => p.id === profileId)
  const currentTitle = currentProfile?.title ?? profileId
  const titleChanged =
    typeof newProfileName === "string" &&
    newProfileName !== "" &&
    newProfileName !== currentTitle

  if (!titleChanged) {
    patchProfileMeta(qc, profileId, {
      status: completeStatus,
      taskId: undefined,
      isCreating: false,
      isInDatabase: true,
      isTrackingStatus: true,
    })
    return profileId
  }

  const keyIsName = profileId === currentTitle
  const nextKey = keyIsName ? newProfileName : profileId
  const nextTitle = preserveTitle ? currentTitle : newProfileName

  if (nextKey !== profileId) {
    remapPendingAudio(profileId, nextKey)
    moveProfileMeta(qc, profileId, nextKey)
  }

  qc.setQueryData<ProfilesListResult>(qk.profiles.list(), (prev) => {
    if (!prev) return prev
    return {
      profiles: prev.profiles.map((p) =>
        p.id === profileId ? { ...p, id: nextKey, title: nextTitle } : p,
      ),
    }
  })

  const currentMeta =
    getProfileMeta(qc, nextKey) ?? getProfileMeta(qc, profileId)
  if (currentMeta) {
    setProfileMeta(qc, nextKey, {
      ...currentMeta,
      status: completeStatus,
      taskId: undefined,
      isCreating: false,
      isInDatabase: true,
      isTrackingStatus: true,
    })
  }

  if (nextKey !== profileId) {
    removeProfileMeta(qc, profileId)
  }

  setSelectedProfileId((prev) => (prev === profileId ? nextKey : prev))
  return nextKey
}

/**
 * Guard for in-flight poll / SSE / hydrate callbacks.
 * - `live === false` after unmount (SSE `closed` alone is not enough).
 * - Missing meta means rename already moved the key — refuse resurrection.
 * - When `taskId` is set, it must still match (generation check).
 */
export function canCommitProfileWrite(
  qc: QueryClient,
  profileId: string,
  opts: { live: boolean; taskId?: string },
): boolean {
  if (!opts.live) return false
  const meta = getProfileMeta(qc, profileId)
  if (!meta) return false
  if (opts.taskId !== undefined && meta.taskId !== opts.taskId) return false
  return true
}
